import { useState, useEffect } from 'react';
import { requester } from 'App';
import styles from './VendorNewDashboard.module.css';

function SettlementCalculator({ userId }) {
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [isLoading, setIsLoading] = useState(false);
    const [calculatedSettlement, setCalculatedSettlement] = useState({
        totalSales: 0,
        totalSupplierCost: 0,
        totalHeadquartersCommission: 0,
        totalStoreOwnerAmount: 0,
        vat: 0,
        finalSettlementAmount: 0
    });

    // 선택한 월의 시작일과 종료일 계산
    const calculateMonthRange = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth(); // 0-11
        
        // 선택한 월의 1일
        const startDate = new Date(year, month, 1);
        
        // 다음 달의 0일 (선택한 월의 마지막 날)
        const endDate = new Date(year, month + 1, 0);
        
        // 날짜 형식 변환 함수
        const formatDate = (date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0'); // 월은 0부터 시작하므로 +1
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };
        
        return {
            startDate: formatDate(startDate), // YYYY-MM-DD 형식
            endDate: formatDate(endDate)      // YYYY-MM-DD 형식
        };
    };

    // 월 변경 함수
    const changeMonth = (amount) => {
        const newDate = new Date(selectedDate);
        newDate.setMonth(newDate.getMonth() + amount);
        setSelectedDate(newDate);
    };

    // 선택한 월의 정산 예정 금액 계산
    const calculateSettlement = () => {
        setIsLoading(true);
        
        const dateRange = calculateMonthRange(selectedDate);
        
        let data = {
            endDate: dateRange.endDate,
            startDate: dateRange.startDate,
            vendorId: userId,
            timeUnit: "month"
        };
        
        requester.getVendorStatisticsAboveThreshold(data, result => {
            setIsLoading(false);
            
            if (result.data && Array.isArray(result.data)) {
                // 각 항목별 합계 계산
                const summary = result.data.reduce((acc, store) => {
                    return {
                        totalSales: acc.totalSales + (store.totalSales || 0),
                        totalSupplierCost: acc.totalSupplierCost + (store.totalSupplierCost || 0),
                        totalHeadquartersCommission: acc.totalHeadquartersCommission + (store.totalHeadquartersCommission || 0),
                        totalStoreOwnerAmount: acc.totalStoreOwnerAmount + (store.totalStoreOwnerAmount || 0)
                    };
                }, {
                    totalSales: 0,
                    totalSupplierCost: 0,
                    totalHeadquartersCommission: 0,
                    totalStoreOwnerAmount: 0
                });
                
                // 부가세 계산 (이달 매출의 10%)
                const vat = Math.round(summary.totalSales * 0.1);
                
                // 부가세를 포함한 요약 정보 업데이트
                const summaryWithVat = {
                    ...summary,
                    vat: vat,
                    // 부가세를 차감한 최종 정산 예정 금액
                    finalSettlementAmount: summary.totalStoreOwnerAmount - vat
                };
                
                setCalculatedSettlement(summaryWithVat);
            }
        });
    };

    // 선택한 월이 변경될 때마다 정산 예정 금액 계산
    useEffect(() => {
        calculateSettlement();
    }, [selectedDate, userId]);

    // 선택한 월 표시 형식
    const formatMonthYear = (date) => {
        return `${date.getFullYear()}년 ${date.getMonth() + 1}월`;
    };

    return (
        <div className={styles.calculatorContainer}>
            <div className={styles.monthSelector}>
                <button 
                    className={styles.monthBtn} 
                    onClick={() => changeMonth(-1)}
                    disabled={isLoading}
                >
                    &lt; 이전 달
                </button>
                <span className={styles.currentMonth}>{formatMonthYear(selectedDate)}</span>
                <button 
                    className={styles.monthBtn} 
                    onClick={() => changeMonth(1)}
                    disabled={isLoading}
                >
                    다음 달 &gt;
                </button>
            </div>
            
            {isLoading ? (
                <div className={styles.loading}>로딩 중...</div>
            ) : (
                <div className={styles.calculatorContent}>
                    <div className={styles.calculatorTitle}>
                        {formatMonthYear(selectedDate)} 정산 예정 금액
                    </div>
                    <div className={styles.calculatorAmount}>
                        {calculatedSettlement.finalSettlementAmount.toLocaleString()}원
                    </div>
                    <div className={styles.calculatorList}>
                        <div className={styles.calculatorItem}>
                            <span className={styles.calculatorLabel}>이달 매출</span>
                            <span className={styles.calculatorValue}>
                                {calculatedSettlement.totalSales.toLocaleString()}원
                            </span>
                        </div>
                        <div className={styles.calculatorItem}>
                            <span className={styles.calculatorLabel}>물품 대금</span>
                            <span className={styles.calculatorValue}>
                                -{calculatedSettlement.totalSupplierCost.toLocaleString()}원
                            </span>
                        </div>
                        <div className={styles.calculatorItem}>
                            <span className={styles.calculatorLabel}>본사 수수료</span>
                            <span className={styles.calculatorValue}>
                                -{calculatedSettlement.totalHeadquartersCommission.toLocaleString()}원
                            </span>
                        </div>
                        <div className={styles.calculatorItem}>
                            <span className={styles.calculatorLabel}>부가세(10%)</span>
                            <span className={styles.calculatorValue}>
                                -{calculatedSettlement.vat.toLocaleString()}원
                            </span>
                        </div>
                        <div className={styles.calculatorItem}>
                            <span className={styles.calculatorLabel}>이번달 정산예정 금액</span>
                            <span className={styles.calculatorValue}>
                                {calculatedSettlement.finalSettlementAmount.toLocaleString()}원
                            </span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default SettlementCalculator; 